import React, { useRef, useState, useEffect, useCallback } from "react";
import Portal from "app/components/portal";
import styles from "./styles.module.scss";

interface PaymentActionsDropdownProps {
  children: React.ReactNode;
  disabled?: boolean;
}

const DROPDOWN_HEIGHT = 65.5;

function PaymentActionsDropdown({ children, disabled }: PaymentActionsDropdownProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, openUpward: false });

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) {
      return;
    }

    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 280;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const openUpward = spaceBelow < DROPDOWN_HEIGHT + 10;

    setPosition({
      top: openUpward ? rect.top - DROPDOWN_HEIGHT : rect.bottom + 4,
      left: rect.right - dropdownWidth,
      openUpward,
    });
  }, []);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) {
      return;
    }

    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (!isOpen) {
      return;
    }

    const target = e.target as Node;
    if (
      buttonRef.current?.contains(target) ||
      dropdownRef.current?.contains(target)
    ) {
      return;
    }
    setIsOpen(false);
  }, [isOpen]);

  const handleScroll = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [handleClickOutside, handleScroll]);

  return (
    <>
      <div
        ref={buttonRef}
        onClick={handleToggle}
        className={styles.actionMenuButton}
      >
        <i className="bullets" />
      </div>
      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            className={styles.portalDropdown}
            style={{
              top: position.top,
              left: position.left,
            }}
            onClick={() => setIsOpen(false)}
          >
            {children}
          </div>
        </Portal>
      )}
    </>
  );
}

export default PaymentActionsDropdown;
